import { frameCount, numberOfChannels, type PcmAudio } from "../types";
import { fromAudioBuffer } from "./decode";

type OfflineAudioContextCtor = typeof OfflineAudioContext;

function resolveOfflineAudioContext(): OfflineAudioContextCtor {
  const ctor =
    globalThis.OfflineAudioContext ??
    (globalThis as { webkitOfflineAudioContext?: OfflineAudioContextCtor })
      .webkitOfflineAudioContext;
  if (!ctor) {
    throw new Error("OfflineAudioContext is not available in this environment");
  }
  return ctor;
}

export interface ResampleOptions {
  /** Target sample rate in Hz (e.g. 16000 for speech). */
  sampleRate: number;
  /** Target channel count. Omit to keep the source channel count. */
  channels?: number;
}

/**
 * Resample (and optionally re-channel) using the browser's high-quality
 * Web Audio resampler. Browser-only.
 *
 * Rendering the source through an `OfflineAudioContext` at the target rate does
 * proper band-limited resampling and channel mixing — far better than naive
 * sample dropping. This is the expensive half of "optimize for transcription"
 * (downsampling speech to 16 kHz mono).
 */
export async function resampleAudio(audio: PcmAudio, options: ResampleOptions): Promise<PcmAudio> {
  const sourceFrames = frameCount(audio);
  const sourceChannels = numberOfChannels(audio);
  if (sourceFrames === 0 || sourceChannels === 0) {
    return { sampleRate: options.sampleRate, channelData: [] };
  }

  const targetChannels = options.channels ?? sourceChannels;
  const targetFrames = Math.max(
    1,
    Math.ceil((sourceFrames * options.sampleRate) / audio.sampleRate)
  );

  const Ctx = resolveOfflineAudioContext();
  const ctx = new Ctx({
    numberOfChannels: targetChannels,
    length: targetFrames,
    sampleRate: options.sampleRate,
  });

  // Source buffer stays at the original rate; playback into a differently-rated
  // context is what triggers the resample.
  const source = ctx.createBuffer(sourceChannels, sourceFrames, audio.sampleRate);
  for (let c = 0; c < sourceChannels; c++) {
    const channel = audio.channelData[c];
    // Our buffers are ArrayBuffer-backed; copyToChannel's type excludes the
    // (impossible here) SharedArrayBuffer case, so narrow it explicitly.
    if (channel) source.copyToChannel(channel as Float32Array<ArrayBuffer>, c);
  }

  const node = ctx.createBufferSource();
  node.buffer = source;
  node.connect(ctx.destination);
  node.start(0);

  const rendered = await ctx.startRendering();
  return fromAudioBuffer(rendered);
}
